/**
 * The files a person hands OS: the transport shape of one, what a reader
 * learned from it, and the limits both ends hold it to.
 *
 * Decided 3 to 6 of docs/plans/setup-from-files.md. A file is kept in
 * `app.agent_file`, owned by its person until a workspace adopts it; the model
 * reads its `FileSummary` and never its bytes. A chat message names a file by
 * reference (`agent-file:<id>`), so no file ever rides in a request body.
 */
import type { LeftOut, LinkKind, SourceFindings, SourceRole } from './setup-sources';
import type { WebsiteFailure, WebsiteFoundInFile, WebsiteSummary } from './websites';

/**
 * What a reader makes of a file. One reader per kind. `xml` is a generic XML
 * file and `tally` the accounting export (a sniff tells them apart); `text` is
 * a `.txt`, a `.tsv`, a Markdown file (`.md`, its pipe tables read as sheets)
 * or a pasted block; `place` is a map place and `page` one public page read
 * by its own reader (a business page, a listing, a store, a catalogue, a
 * shared table, a video or blog, an app's pages).
 */
export const FILE_KINDS = [
  'sheet',
  'csv',
  'tally',
  'pdf',
  'image',
  'website',
  'xls',
  'docx',
  'pptx',
  'json',
  'xml',
  'text',
  'place',
  'page',
] as const;
export type FileKind = (typeof FILE_KINDS)[number];

/**
 * Where a file stands. `pending` until its bytes are proven, `ready` once they
 * are and the reading has begun, `read` when the summary is written, `failed`
 * when the reader gave up (the row's `failure` says why), `needs` while a
 * setup source waits for the person (a file's password, whether a place is
 * theirs, whether to read a link heard on a call). The sweep never touches a
 * row that needs the person.
 */
export const FILE_STATUSES = ['pending', 'ready', 'read', 'failed', 'needs'] as const;
export type FileStatus = (typeof FILE_STATUSES)[number];

/** Why a file was refused or could not be read. One line each in the UI. `too_dark` is a photo too dark to read any line; `blurred` is blur. */
export const FILE_FAILURES = ['too_big', 'wrong_type', 'password', 'unreadable', 'blurred', 'empty', 'nothing_found', 'too_dark'] as const;
export type FileFailure = (typeof FILE_FAILURES)[number];

/** Whether a failure is a file's own, and not a website's. */
export const isFileFailure = (failure: string | null | undefined): failure is FileFailure =>
  typeof failure === 'string' && (FILE_FAILURES as readonly string[]).includes(failure);

/**
 * The kinds a file with bytes can be: every kind but the three that are read
 * from an address: a website, a business's site (Decided 23), a map place and
 * one public page. Each is a row with no bytes, its address in `sourceUrl`.
 */
export type UploadKind = Exclude<FileKind, 'website' | 'place' | 'page'>;

/** Where a file came in. */
export const FILE_ORIGINS = ['setup', 'chat', 'record', 'list', 'call'] as const;
export type FileOrigin = (typeof FILE_ORIGINS)[number];

/** One file as the API answers it. The object key never leaves the server. */
export type AgentFile = {
  readonly id: string;
  /** Null while the person is still setting up: no workspace owns it yet. */
  readonly tenantId: string | null;
  readonly ownerUserId: string;
  readonly name: string;
  readonly mime: string;
  readonly kind: FileKind;
  readonly size: number;
  readonly status: FileStatus;
  /** A website's failures are its own (`WebsiteFailure`); a file's are `FileFailure`. */
  readonly failure: FileFailure | WebsiteFailure | null;
  /**
   * Null until the file is read. A website's is a `WebsiteSummary`, written
   * while it is read too (its `progress`); read it with `fileSummaryOf` or
   * `websiteSummaryOf`.
   */
  readonly summary: FileSummary | WebsiteSummary | null;
  readonly origin: FileOrigin;
  /** The record or thread the file came in on, when it came in on one. */
  readonly originRef: string | null;
  /** Pages of a PDF or a scan; null for a sheet. */
  readonly pages: number | null;
  readonly createdAt: string;
  /** The address a website was read from; null for a file. */
  readonly sourceUrl: string | null;
  /** The setup session a source belongs to; null for a workspace's files. */
  readonly setupSessionId: string | null;
  /** The part a setup source plays (`setup-sources.ts`); null for a workspace's files. */
  readonly role: SourceRole | null;
  /** What kind of link a source came from; null for a file. */
  readonly linkKind: LinkKind | null;
  /** What a setup source taught, typed; null until it is read, and for a workspace's files. */
  readonly findings: SourceFindings | null;
  /** When the person chose to go on without it; null while it counts. */
  readonly leftAt: string | null;
};

/* ------------------------------------------------------------------ limits */

/** One file may be up to 50 MB. */
export const AGENT_FILE_MAX_BYTES = 50 * 1024 * 1024;

/** Up to 20 files in one go. */
export const AGENT_FILES_AT_ONCE = 20;

/**
 * The types the picker offers, by MIME type: each one a reader reads.
 * `.doc`, `.ppt` and `.zip` are not read.
 */
export const AGENT_FILE_MIME_TYPES: readonly string[] = [
  'text/csv',
  'application/csv',
  'application/vnd.ms-excel.csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/json',
  'text/plain',
  'text/tab-separated-values',
  'text/markdown',
  'text/x-markdown',
  'application/xml',
  'text/xml',
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
];

/** The same, by extension: a browser often sends no type, or a wrong one, for a CSV or an XML export. */
export const AGENT_FILE_EXTENSIONS: readonly string[] = [
  '.csv',
  '.xlsx',
  '.xls',
  '.docx',
  '.pptx',
  '.json',
  '.txt',
  '.tsv',
  '.md',
  '.markdown',
  '.xml',
  '.pdf',
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  '.heic',
  '.heif',
];

const extensionOf = (name: string): string => {
  const dot = name.lastIndexOf('.');
  return dot < 0 ? '' : name.slice(dot).toLowerCase().trim();
};

const KIND_BY_EXTENSION: Readonly<Record<string, UploadKind>> = {
  '.csv': 'csv',
  '.xlsx': 'sheet',
  '.xls': 'xls',
  '.docx': 'docx',
  '.pptx': 'pptx',
  '.json': 'json',
  '.txt': 'text',
  '.tsv': 'text',
  '.md': 'text',
  '.markdown': 'text',
  '.xml': 'xml',
  '.pdf': 'pdf',
  '.jpg': 'image',
  '.jpeg': 'image',
  '.png': 'image',
  '.webp': 'image',
  '.heic': 'image',
  '.heif': 'image',
};

const KIND_BY_MIME: Readonly<Record<string, UploadKind>> = {
  'text/csv': 'csv',
  'application/csv': 'csv',
  'application/vnd.ms-excel.csv': 'csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'sheet',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
  'application/json': 'json',
  'text/plain': 'text',
  'text/tab-separated-values': 'text',
  'text/markdown': 'text',
  'text/x-markdown': 'text',
  'application/xml': 'xml',
  'text/xml': 'xml',
  'application/pdf': 'pdf',
  'image/jpeg': 'image',
  'image/png': 'image',
  'image/webp': 'image',
  'image/heic': 'image',
  'image/heif': 'image',
};

/**
 * What a file is, from its name and its declared type, or null when OS does
 * not read it. The extension wins over the type: a browser calls a CSV
 * `application/vnd.ms-excel` and an XML export `text/plain`. The bytes are
 * checked again at commit, so this decides only what to try.
 */
export const fileKindOf = (name: string, mime: string): UploadKind | null => {
  const byName = KIND_BY_EXTENSION[extensionOf(name)];
  if (byName !== undefined) return byName;
  const type = mime.toLowerCase().split(';')[0]?.trim() ?? '';
  return KIND_BY_MIME[type] ?? null;
};

/** The kinds a reader reads: every upload kind. */
const READ_NOW: ReadonlySet<UploadKind> = new Set<UploadKind>(['sheet', 'csv', 'tally', 'pdf', 'image', 'xls', 'docx', 'pptx', 'json', 'xml', 'text']);

/**
 * The kind a file is taken as today, or null when no reader reads it yet: the
 * kind `fileKindOf` names. The client refuses on it and the server keeps a new
 * file as it; the commit's sniff then says what the bytes are (an `.xml` that
 * is a Tally export is read as one).
 */
export const fileTakenAs = (name: string, mime: string): UploadKind | null => {
  const kind = fileKindOf(name, mime);
  return kind !== null && READ_NOW.has(kind) ? kind : null;
};

/** Whether a file's size is one OS takes. */
export const fileSizeAllowed = (size: number): boolean => Number.isFinite(size) && size > 0 && size <= AGENT_FILE_MAX_BYTES;

/** Whether a summary is a website's. */
export const isWebsiteSummary = (summary: FileSummary | WebsiteSummary | null | undefined): summary is WebsiteSummary =>
  summary !== null && summary !== undefined && summary.kind === 'website';

/** A file's own summary, or null for a website or a file not read yet. */
export const fileSummaryOf = (file: { readonly summary: FileSummary | WebsiteSummary | null }): FileSummary | null =>
  file.summary === null || isWebsiteSummary(file.summary) ? null : file.summary;

/** A website's summary, or null for a file or a website not started. */
export const websiteSummaryOf = (file: { readonly summary: FileSummary | WebsiteSummary | null }): WebsiteSummary | null =>
  isWebsiteSummary(file.summary) ? file.summary : null;

/* --------------------------------------------------------------- reference */

const REF_PREFIX = 'agent-file:';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The URL a message's file part carries for a kept file. Never bytes. */
export const agentFileRef = (id: string): string => `${REF_PREFIX}${id}`;

/** The file id an `agent-file:` URL names, or null when it names none. */
export const agentFileIdOf = (url: string): string | null => {
  if (!url.startsWith(REF_PREFIX)) return null;
  const id = url.slice(REF_PREFIX.length);
  return UUID.test(id) ? id.toLowerCase() : null;
};

/* ----------------------------------------------------------------- summary */

/** What a column holds, as its values read. `mixed` when it changes format part way. */
export const COLUMN_KINDS = ['text', 'number', 'money', 'date', 'percent', 'boolean', 'empty', 'mixed'] as const;
export type ColumnKind = (typeof COLUMN_KINDS)[number];

/** One column of a sheet, profiled over every row. */
export type ColumnProfile = {
  /** Zero-based, in the sheet as read. */
  readonly index: number;
  /** The header's words; `Column 3` style when the sheet has none. */
  readonly name: string;
  readonly kind: ColumnKind;
  readonly blanks: number;
  readonly distinct: number;
  /** As read: a number for numbers and money, an ISO day for dates, else the text. Null when the column is empty. */
  readonly min: string | number | null;
  readonly max: string | number | null;
  /** Up to five values, as written. */
  readonly examples: readonly string[];
  /**
   * Every distinct value, sorted, when a text column has few of them (at most
   * twenty): a choice the setup makes into a select's options. Absent otherwise.
   */
  readonly values?: readonly string[];
  /**
   * When a column's format changes part way (a date column that turns from
   * 01/04/2026 to 2026-04-01, an amount that turns into text), the first data
   * row, zero-based, written in the other format.
   */
  readonly changesAt?: number;
};

/** One sheet: a tab of a workbook, a CSV, the vouchers of a Tally export. */
export type SheetSummary = {
  readonly name: string;
  /** The zero-based row the header was found on; null when the sheet has none. */
  readonly headerRow: number | null;
  /** Rows above the header that read as a title, kept for the model to read. */
  readonly titleRows: readonly string[];
  readonly columns: readonly ColumnProfile[];
  /** Data rows below the header, the totals row not counted. */
  readonly rowCount: number;
  /** About 20 rows from the start, the middle and the end, each with its zero-based row number. */
  readonly sample: readonly { readonly row: number; readonly cells: readonly string[] }[];
  /** A row that sums the rows above it, when one was found. */
  readonly totals: { readonly row: number; readonly cells: readonly string[] } | null;
  /** Columns left out because they look like bank account, card or Aadhaar numbers (Decided 21). */
  readonly leftOut: readonly string[];
  /** Which record the sheet fills, when the decision model was asked (step 1.6). */
  readonly fills?: FileDecision<RecordGuess>;
  /** A Tally export's ledgers: how many there are of each group, counted over every ledger (Sundry Debtors, Sundry Creditors …). */
  readonly groups?: Readonly<Record<string, number>>;
};

/** One page of a PDF or a photo: the lines read off it. */
export type PageSummary = {
  readonly page: number;
  readonly lines: readonly string[];
};

/** What a file is about. The decision model's first question (step 1.6). */
export const FILE_ABOUTS = [
  'sales',
  'purchases',
  'expenses',
  'stock',
  'staff',
  'contacts',
  'statement',
  'accounts',
  'paper',
  'other',
] as const;
export type FileAbout = (typeof FILE_ABOUTS)[number];

/** Which record a sheet fills: the same list, less the two that fill none. */
export const RECORD_GUESSES = ['sales', 'purchases', 'expenses', 'stock', 'staff', 'contacts', 'statement', 'none'] as const;
export type RecordGuess = (typeof RECORD_GUESSES)[number];

/** Below this, OS asks the person about a file or a sheet, and sets a row aside. */
export const DECISION_SURE = 0.8;

/** A decision and how sure it was. `ask` is true when the person must say. */
export type FileDecision<T extends string> = {
  readonly answer: T;
  readonly probability: number;
  readonly ask: boolean;
};

/** Whether an answer of this probability is kept, or asked about. */
export const decisionOf = <T extends string>(answer: T, probability: number): FileDecision<T> => ({
  answer,
  probability,
  ask: !(probability >= DECISION_SURE),
});

/**
 * The record a sheet fills, only when the decision model was sure of it: an
 * unsure answer is a question for the person, never a fact to act on. Null
 * when the sheet was not asked about, the model was unsure, or it fills none.
 */
export const sureFills = (sheet: Pick<SheetSummary, 'fills'>): Exclude<RecordGuess, 'none'> | null => {
  const fills = sheet.fills;
  if (fills === undefined || fills.ask || fills.answer === 'none') return null;
  return fills.answer;
};

/** Everything the model reads about one file. Never the file itself. */
export type FileSummary = {
  readonly kind: UploadKind;
  /** Sheets for a workbook, a CSV or a Tally export, and a text PDF's tables (`Table 1`, `Table 2`); empty for a photo or a scan. */
  readonly sheets: readonly SheetSummary[];
  /** Pages for a PDF or a photo, as many as were read. */
  readonly pages: readonly PageSummary[];
  /** How many pages the file has; null for a sheet. */
  readonly pagesTotal: number | null;
  /** How many were read now. A scan over 10 pages is read for 10 at setup; the import reads the rest. */
  readonly pagesRead: number | null;
  /** A PDF with no text layer, or a photo: read by the model, not by code. */
  readonly scan: boolean;
  /** Total data rows across sheets. */
  readonly rowsFound: number;
  /** A BCP 47 tag for the language the words are in, `und` when unknown. */
  readonly language: string;
  /** A PDF or a photo's line that reads as its total, when one was found. */
  readonly totalLine?: { readonly page: number; readonly line: string } | null;
  /**
   * True for a PDF with a text layer and no table found on its pages: its
   * lines are for the model, and it has no rows to import, so an import shows
   * it as not read. Absent on every other file and on a PDF read before its
   * tables were (treat absent as false).
   */
  readonly noTable?: boolean;
  /**
   * The names of a workbook's sheets past the first `SHEETS_MAX` (20), left
   * unread (Decided 5 of docs/plans/files-paging-and-fixes.md). Absent, or
   * empty, when every sheet was read.
   */
  readonly sheetsSkipped?: readonly string[];
  /** What the file is about, when the decision model was asked (step 1.6). */
  readonly about?: FileDecision<FileAbout>;
  /** What the readers masked in the text kept (ID numbers, phones) and the columns left out whole, by kind. */
  readonly redacted?: readonly LeftOut[];
  /** A Word document's or a deck's pictures, video and sound: counted, never read. */
  readonly media?: FileMedia;
  /** Read with its own password (step 4.6): its rows are read again at import, the password asked again. */
  readonly locked?: true;
  /**
   * Whether the file is the business's own paper or a party's (a supplier's
   * bill, a bank's or a party's statement, a contact list), when the decision
   * model was asked. Not a kind: only the look for the business's website in
   * its files reads it (`web/find-in-files.ts`), so a party's letterhead is
   * never taken for the business's own. No model and no screen is shown it.
   */
  readonly whose?: FileDecision<'own' | 'party'>;
};

/** A bill a scan read: who sold to whom, its lines and totals, as written. */
export type ScanBill = {
  readonly seller: string | null;
  readonly sellerGstin: string | null;
  readonly buyer: string | null;
  readonly buyerGstin: string | null;
  readonly number: string | null;
  readonly date: string | null;
  readonly items: readonly {
    readonly name: string;
    readonly qty: string;
    readonly rate: string;
    readonly amount: string;
    readonly batch?: string;
    readonly expiry?: string;
  }[];
  readonly tax: string | null;
  readonly total: string | null;
  readonly mode: string | null;
};

/** A table a scan read: its header and its rows, each cell with how sure the reading was (0 to 1). */
export type ExtractedSheet = {
  readonly name: string;
  readonly columns: readonly string[];
  readonly rows: readonly { readonly cells: readonly string[]; readonly sure: readonly number[] }[];
};

/**
 * What a model read off a photo or a scan beyond its lines (step 4.7), kept
 * on `app.agent_file.extracted` for the import after Build: its tables, the
 * rows struck through and dropped, the pages too dark to read, whether it is
 * a screenshot of an app, and a bill.
 */
export type ScanExtracted = {
  readonly sheets: readonly ExtractedSheet[];
  readonly crossedOut: number;
  readonly darkPages: readonly number[];
  readonly screen: boolean;
  readonly bill: ScanBill | null;
};

/** A cell read less surely than this is looked at before it comes in. */
export const SCAN_SURE = 0.8;

/** Pictures counted; video and sound counted and their size, skipped. */
export type FileMedia = { readonly images: number; readonly videos: number; readonly audio: number; readonly skippedBytes: number };

/** Pages a scan is read for during setup, and per model call. */
export const SCAN_PAGES_PER_GROUP = 10;

/* ------------------------------------------------------------------ routes */

/** POST /v1/files: what the client says it is about to send. */
export type AgentFileCreateRequest = {
  readonly name: string;
  readonly size: number;
  readonly mime: string;
  readonly origin: FileOrigin;
  readonly originRef?: string | null;
};

/** The row, and where to PUT the bytes. */
export type AgentFileCreateResponse = {
  readonly file: AgentFile;
  readonly uploadUrl: string;
  /** Headers the PUT must send; part of what was signed. */
  readonly uploadHeaders: Readonly<Record<string, string>>;
  readonly expiresAt: string;
};

/** POST /v1/files/:id/commit and GET /v1/files/:id. */
export type AgentFileResponse = { readonly file: AgentFile };

/** One record something came into from a file, and how many of its entries did. */
export type AgentFileCameInRecord = { readonly recordId: string; readonly name: string; readonly entries: number };

/**
 * What came in from a file: the entries saved from it, the records they went
 * to (each by name, the most first), and the rows of it still set aside for
 * the person.
 */
export type AgentFileCameIn = {
  readonly entries: number;
  readonly records: number;
  readonly inRecords: readonly AgentFileCameInRecord[];
  readonly aside: number;
};

/** One thing OS took from a file: a standing fact of source `file` that names it. */
export type AgentFileTook = { readonly id: string; readonly text: string };

/** One file as What OS knows › Files lists it (Decided 20). */
export type AgentFileListItem = AgentFile & {
  readonly cameIn: AgentFileCameIn;
  /** What OS took from it, oldest first. Empty for a file it took nothing from. */
  readonly took: readonly AgentFileTook[];
};

/** GET /v1/files: a workspace's files, newest first (admins only), or the person's setup files. */
export type AgentFileListResponse = {
  readonly files: readonly AgentFileListItem[];
};

/** GET /v1/files/:id/download: a short-lived read link. */
export type AgentFileDownloadResponse = { readonly url: string; readonly expiresAt: string };

/** DELETE /v1/files/:id?undo=keep|take_out. */
export const FILE_UNDO = ['keep', 'take_out'] as const;
export type FileUndo = (typeof FILE_UNDO)[number];
export type AgentFileDeleteResponse = { readonly id: string; readonly deleted: true };

/* ---------------------------------------------------------------- learned */

/**
 * What OS learned from the files read in setup (Decided 8 of
 * docs/plans/setup-from-files.md): one structured call over the summaries,
 * answered once per set of read files and sent to the client as the data part
 * `data-setup-learned`. Each row says where it came from and whether it was
 * read in the file or is a guess; at most three questions the files did not
 * answer; where two files disagree; and the files the decision model was not
 * sure of, with the answers to choose from.
 */
export type LearnedRow = {
  /** What the row is about, in a word or two: Workspace, How you sell, Who works. */
  readonly about: string;
  readonly text: string;
  readonly fileId: string;
  /** True when it is read in the file; false for a guess the person should check. */
  readonly sure: boolean;
};

export type LearnedQuestion = { readonly question: string; readonly choices: readonly string[] };

/** Two files that say different things about one thing, each in its own words, and how to settle it. */
export type LearnedConflict = {
  readonly about: string;
  readonly says: readonly { readonly fileId: string; readonly text: string }[];
  readonly question: string;
  readonly choices: readonly string[];
};

/** A file the decision model was not sure of (below `DECISION_SURE`): what it might be. */
export type LearnedAskKind = { readonly fileId: string; readonly choices: readonly FileAbout[] };

export type SetupLearned = {
  /** The read files this was learned from, in the order they were handed over. */
  readonly fileIds: readonly string[];
  /** Each file's name, so a source can be named without the upload queue. */
  readonly names: Readonly<Record<string, string>>;
  readonly learned: readonly LearnedRow[];
  readonly questions: readonly LearnedQuestion[];
  readonly conflicts: readonly LearnedConflict[];
  readonly askKind: readonly LearnedAskKind[];
  /**
   * Website addresses found in the files (Decided 28): `read` ones are being
   * read already; `ask` ones wait for the person, and a Yes sends
   * POST /v1/websites with `foundIn`. Absent on a part learned before Phase 9.
   */
  readonly websites?: readonly WebsiteFoundInFile[];
};

/** The data part's type on the wire. */
export const SETUP_LEARNED_PART = 'data-setup-learned';

/** At most this many questions the files did not answer. */
export const LEARNED_QUESTIONS_MAX = 3;

/** Whether two sets of file ids are the same set, in any order. */
export const sameFileSet = (a: readonly string[], b: readonly string[]): boolean => {
  if (a.length !== b.length) return false;
  const held = new Set(a);
  return b.every((id) => held.has(id));
};

/** Whether the learned part asks the person anything before the talk goes on. */
export const learnedAsks = (learned: SetupLearned): boolean =>
  learned.questions.length > 0 ||
  learned.conflicts.length > 0 ||
  learned.askKind.length > 0 ||
  (learned.websites ?? []).some((one) => one.state === 'ask');

/* ------------------------------------------------------------- in a chat */

/**
 * A file of up to this many rows is brought in as one entries card
 * (`propose_entries`); a file of more is offered as an import, the two ways
 * (Decided 19 of docs/plans/setup-from-files.md).
 */
export const ENTRIES_FROM_FILE_MAX = 24;

/** One sheet of a file the import offer would bring in, and the record it goes into when one was found. */
export type ImportOfferPart = {
  readonly fileId: string;
  readonly sheet: string;
  readonly rows: number;
  readonly recordId: string | null;
  readonly recordName: string | null;
};

/**
 * What `propose_import` answers: the files and their sheets, each with its
 * rows and where it would go, for the card that offers the two ways. Nothing
 * is planned or saved: the card's choice posts POST /v1/imports with the
 * files and each sheet's record as `targets`, and starts the job in the mode
 * picked. Kept out of `AgentToolOutput`
 * on purpose, as the role kinds are: only a client that draws it is sent it.
 */
export type ImportOfferOutput = {
  readonly kind: 'import_offer';
  readonly files: readonly { readonly id: string; readonly name: string; readonly kind: FileKind; readonly rows: number }[];
  readonly parts: readonly ImportOfferPart[];
  readonly rows: number;
};

/** Whether a tool's output is an import offer, read defensively: a stored part's shape was never checked. */
export const isImportOfferOutput = (value: unknown): value is ImportOfferOutput => {
  if (typeof value !== 'object' || value === null) return false;
  const held = value as Partial<Record<keyof ImportOfferOutput, unknown>>;
  return held.kind === 'import_offer' && Array.isArray(held.files) && Array.isArray(held.parts) && typeof held.rows === 'number';
};
